const API_BASE =
  import.meta.env.VITE_API_BASE ?? 'https://health-app-worker.ramis-zholdoshev.workers.dev'

/**
 * JWT живёт только в памяти модуля. В localStorage его класть нельзя: в
 * Telegram WebView уже был реальный угон сессий через XSS, а при перезапуске
 * Mini App повторный логин по initData всё равно бесшовный.
 */
let authToken: string | null = null

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  headers.set('Content-Type', 'application/json')
  if (authToken) headers.set('Authorization', `Bearer ${authToken}`)

  let response: Response
  try {
    response = await fetch(`${API_BASE}${path}`, { ...init, headers })
  } catch {
    throw new ApiError('Нет связи с сервером. Проверь интернет.', 0)
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string }
    throw new ApiError(body.error ?? `Ошибка ${response.status}`, response.status)
  }
  return response.json() as Promise<T>
}

export async function login(initDataRaw: string): Promise<void> {
  const { token } = await request<{ token: string; expiresAt: number }>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ initData: initDataRaw }),
  })
  authToken = token
}

/* ─────────────── Сегодня ─────────────── */

export interface PlanItem {
  kind: string
  details: Record<string, unknown>
  sort_order: number
  /** Не из плана дня: прогулка в беговой день, лишний зал, отметка из чата. */
  extra: boolean
}

/** Активность, которую засчитали часы, а не человек галочкой. */
export interface AutoActivity {
  kind: string
  label: string
  detail: string
  source: 'watch' | 'steps'
}

export interface TodayResponse {
  date: string
  targets: { kcal: number; proteinG: number; steps: number }
  eaten: { kcal: number; proteinG: number; fatG: number; carbG: number; meals: number }
  /** Пунктов на день несколько: зарядка каждый день плюс тренировка. */
  plan: PlanItem[]
  done: string[]
  /** Осознанно пропущенные сегодня — это не то же самое, что пустая строка. */
  skipped: string[]
  /** Закрыто данными часов; явная отметка человека всё равно главнее. */
  auto: AutoActivity[]
}

export const fetchToday = () => request<TodayResponse>('/api/today')

/* ─────────────── Еда ─────────────── */

export interface MealItemRow {
  id: string
  name: string
  grams: number | null
  kcal: number
}

export interface MealRow {
  id: string
  kind: string | null
  source: string
  status: 'draft' | 'confirmed'
  eaten_at: string
  total_kcal: number
  protein_g: number
  fat_g: number
  carb_g: number
  kcal_low: number | null
  kcal_high: number | null
  ai_assumptions: string[]
  meal_items: MealItemRow[]
}

export interface FoodPreset {
  id: string
  name: string
  emoji: string | null
  kcal: number
  is_drink: boolean
}

export const fetchMeals = (date?: string) =>
  request<{ date: string; meals: MealRow[] }>(`/api/meals${date ? `?date=${date}` : ''}`)

export const fetchPresets = () => request<{ presets: FoodPreset[] }>('/api/meals/presets')

export const addPreset = (presetId: string) =>
  request<{ mealId: string }>(`/api/meals/preset/${presetId}`, { method: 'POST' })

export const scaleMeal = (mealId: string, factor: number) =>
  request<{ ok: true }>(`/api/meals/${mealId}`, {
    method: 'PATCH',
    body: JSON.stringify({ factor }),
  })

export const confirmMeal = (mealId: string) =>
  request<{ ok: true }>(`/api/meals/${mealId}`, {
    method: 'PATCH',
    body: JSON.stringify({ confirm: true }),
  })

export const removeMeal = (mealId: string) =>
  request<{ ok: true }>(`/api/meals/${mealId}`, { method: 'DELETE' })

export const analyzePhoto = (imageBase64: string, mediaType: string, note?: string) =>
  request<{ mealId: string }>('/api/meals/analyze', {
    method: 'POST',
    body: JSON.stringify({ imageBase64, mediaType, note }),
  })

/* ─────────────── Тренировка ─────────────── */

export interface ExerciseSet {
  id?: string
  set_no: number
  weight_kg: number | null
  reps: number | null
  seconds: number | null
}

export interface Exercise {
  id: string
  name: string
  pair_no: number
  zone: string | null
  target_sets: number
  target_reps: number
  unit: 'reps' | 'seconds'
  step_kg: number
  note: string | null
  /** Добавлено вручную на сегодня — можно убрать, не трогая программу. */
  added_manually: boolean
  last_sets: ExerciseSet[]
  suggested_weight_kg: number | null
  today_sets: ExerciseSet[]
}

export interface DayActivity {
  kind: string
  label: string
  status: 'done' | 'skipped'
  source: 'manual' | 'watch' | 'steps'
  detail: string
}

export interface WorkoutToday {
  date: string
  /** Что сегодня по плану — зал, бег, интервалы, прогулка или отдых. */
  focus: string
  focus_details: Record<string, unknown>
  activities: DayActivity[]
  gym_day: 'A' | 'B'
  session_id: string | null
  completed: boolean
  back_pain_0_10: number | null
  exercises: Exercise[]
}

export const fetchWorkoutToday = () => request<WorkoutToday>('/api/workouts/today')

export const saveSession = (payload: {
  kind: string
  completed?: boolean
  skipped?: boolean
  run_km?: number
  duration_min?: number
  back_pain_0_10?: number
}) =>
  request<{ sessionId: string }>('/api/workouts/session', {
    method: 'POST',
    body: JSON.stringify(payload),
  })

export const saveSet = (payload: {
  sessionId: string
  exerciseId: string
  setNo: number
  weightKg?: number | null
  reps?: number | null
  seconds?: number | null
}) =>
  request<{ ok: true }>('/api/workouts/set', {
    method: 'POST',
    body: JSON.stringify(payload),
  })

export const deleteSet = (payload: { sessionId: string; exerciseId: string; setNo: number }) =>
  request<{ ok: true; renumbered: number }>('/api/workouts/set', {
    method: 'DELETE',
    body: JSON.stringify(payload),
  })

/* ─────────────── Свои упражнения ─────────────── */

export interface CatalogExercise {
  id: string
  name: string
  gym_day: 'A' | 'B' | 'custom'
  zone: string | null
  target_sets: number
  target_reps: number
  unit: 'reps' | 'seconds'
}

export const fetchExerciseCatalog = () =>
  request<{ exercises: CatalogExercise[] }>('/api/workouts/exercises')

export const addExerciseToday = (payload: {
  exerciseId?: string
  name?: string
  targetSets?: number
  targetReps?: number
  unit?: 'reps' | 'seconds'
  /** 'today' — только на сегодня, 'A' или 'B' — навсегда в программу этого дня. */
  scope?: 'today' | 'A' | 'B'
}) =>
  request<{ ok: true; exerciseId: string; sessionId: string }>('/api/workouts/today/exercise', {
    method: 'POST',
    body: JSON.stringify(payload),
  })

export const removeExerciseToday = (exerciseId: string) =>
  request<{ ok: true }>(`/api/workouts/today/exercise/${exerciseId}`, { method: 'DELETE' })

/* ─────────────── Замеры ─────────────── */

export interface Measurement {
  local_date: string
  weight_kg: number | null
  waist_cm: number | null
  back_pain_0_10: number | null
}

export const fetchMeasurements = () =>
  request<{ measurements: Measurement[]; progress: { weight_kg: number; waist_cm: number } | null }>(
    '/api/measurements',
  )

export const saveMeasurement = (payload: {
  weightKg?: number
  waistCm?: number
  backPain?: number
}) =>
  request<{ ok: true }>('/api/measurements', {
    method: 'POST',
    body: JSON.stringify(payload),
  })

/* ─────────────── Здоровье (Apple Watch) ─────────────── */

export interface MetricSummary {
  metric: string
  label: string
  unit: string | null
  digits: number
  /** Показывать в основной сетке, а не в общем списке. */
  primary: boolean
  /** Имя цветового токена для графика — задаётся на сервере, в справочнике метрик. */
  tint: string
  today: number | null
  latest: number | null
  /** День, за который есть последнее значение: данные приходят пачками. */
  latest_day: string | null
  week_average: number | null
  prev_week_average: number | null
  best: number | null
  /** 14 календарных дней от сегодня назад; null — день без данных. */
  days: { day: string; value: number | null }[]
}

export interface HealthWorkout {
  id: string
  activity: string
  distance_km: number | null
  duration_min: number | null
  avg_heart_rate: number | null
}

export interface HealthSummary {
  connected: boolean
  last_sync: string | null
  stale: boolean
  steps_target: number
  summary: MetricSummary[]
  workouts: HealthWorkout[]
}

export interface Insight {
  period: string
  period_start: string
  content_md: string
  model: string
}

export const fetchHealth = () => request<HealthSummary>('/api/health')

export const fetchInsight = () => request<{ insight: Insight | null }>('/api/health/insight')

/* ─────────────── Анализы и InBody ─────────────── */

export interface LabValue {
  name: string
  value: number
  unit: string
  ref_low: number | null
  ref_high: number | null
  confidence: 'low' | 'medium' | 'high'
}

export interface InBodyValues {
  weight_kg: number | null
  skeletal_muscle_kg: number | null
  body_fat_kg: number | null
  body_fat_pct: number | null
  bmi: number | null
  visceral_fat_level: number | null
  total_body_water_l: number | null
  ecw_tbw: number | null
  bmr_kcal: number | null
  inbody_score: number | null
}

export interface LabParseResult {
  kind: 'blood' | 'inbody' | 'other'
  taken_at: string | null
  lab_name: string | null
  values: LabValue[]
  inbody: InBodyValues | null
  warnings: string[]
}

export interface LabDraft {
  id: string
  taken_at: string
  lab_name: string | null
  kind: 'blood' | 'inbody' | 'other'
  raw_ai_response: LabParseResult
  source_file_path: string | null
}

export interface SavedLabValue {
  biomarker_id: string
  value: number
  unit: string
  ref_low: number | null
  ref_high: number | null
  out_of_range: boolean
  biomarkers: { canonical_name: string; category: string | null }
}

export interface LabReport {
  id: string
  taken_at: string
  lab_name: string | null
  kind: string
  ai_interpretation: string | null
  lab_values: SavedLabValue[]
}

export interface InBodyRow extends InBodyValues {
  id: string
  taken_at: string
}

export const fetchLabs = () => request<{ reports: LabReport[]; inbody: InBodyRow[] }>('/api/labs')

export const fetchLabDrafts = () => request<{ drafts: LabDraft[] }>('/api/labs/pending')

export const fetchLabFileUrl = (reportId: string) =>
  request<{ url: string }>(`/api/labs/${reportId}/file`)

export const confirmLab = (
  reportId: string,
  payload: { values?: LabValue[]; inbody?: InBodyValues; takenAt?: string },
) =>
  request<{ saved: number; rejected: string[] }>(`/api/labs/${reportId}/confirm`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })

export const removeLab = (reportId: string) =>
  request<{ ok: true }>(`/api/labs/${reportId}`, { method: 'DELETE' })

export const buildLabInsight = (reportId: string) =>
  request<{ text: string }>(`/api/labs/${reportId}/insight`, { method: 'POST' })

export const fetchBiomarkerHistory = (biomarkerId: string) =>
  request<{
    points: { taken_at: string; value: number; unit: string; ref_low: number | null; ref_high: number | null }[]
  }>(`/api/labs/biomarker/${biomarkerId}`)

/**
 * Файл уходит сырым телом, а не base64 в JSON: кодирование раздуло бы бланк
 * на треть, а мобильный интернет тут узкое место.
 */
export async function uploadLab(file: File): Promise<{ reportId: string; parsed: LabParseResult; fileStored: boolean }> {
  const headers = new Headers({ 'Content-Type': file.type })
  if (authToken) headers.set('Authorization', `Bearer ${authToken}`)

  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/labs/upload`, { method: 'POST', headers, body: file })
  } catch {
    throw new ApiError('Нет связи с сервером. Проверь интернет.', 0)
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string }
    throw new ApiError(body.error ?? `Ошибка ${response.status}`, response.status)
  }
  return response.json() as Promise<{ reportId: string; parsed: LabParseResult; fileStored: boolean }>
}
